import { useCallback, useMemo } from 'react';

import {
  useDataRefId,
  usePagination,
  useMutation,
  useRefetch,
} from '@bingads-webui-react/overreact';

import { environmentLookupFn } from '../../../../../../env/env-instance';
import { addSpec } from '../../__specs/coll/add-spec';
import { destroySpec } from '../../__specs/coll/destroy-spec';
import { fetchSpec, refetchSpec } from '../../__specs/coll/fetch-spec';
import { mutationSpec } from '../../__specs/coll/mutation-spec';
import { dataRefIdDecorator } from './hook-decorators';

const DEFAULT_CONFIG = {};

export function useTags(variables, config = DEFAULT_CONFIG) {
  const dataRefId = useDataRefId(dataRefIdDecorator(), environmentLookupFn);

  const defaultConfig = useMemo(() => ({
    refetch: config.refetch || {},
    mutation: config.mutation || {},
    destroy: config.destroy || {},
    pagination: config.pagination || {},
    create: config.add || {},
  }), [config]);

  const {
    refetch: refetchConfig,
    mutation: mutationConfig,
    destroy: destroyConfig,
    pagination: paginationConfig,
    create: createConfig,
  } = defaultConfig;

  const expandedPaginationConfig = useMemo(() => ({
    fetchVariables: {
      ...variables,
    },
    ...paginationConfig,
  }), [variables, paginationConfig]);

  const [data, paginationMethods] = usePagination(dataRefId, fetchSpec, expandedPaginationConfig);
  const mutateFn = useMutation(dataRefId, mutationSpec, mutationConfig);
  const destroyFn = useMutation(dataRefId, destroySpec, destroyConfig);
  const createFn = useMutation(dataRefId, addSpec, createConfig);
  const refetchFn = useRefetch(dataRefId, refetchSpec, refetchConfig);

  const update = useCallback((newData, ...rest) => {
    mutateFn({
      locator: {
        descriptor: {
          ...variables.locator.descriptor,
          tag_id: newData.tag_id || newData.Id,
        },
      },
    }, newData, ...rest);
  }, [mutateFn, variables]);

  const destroy = useCallback((additionalDescriptor, ...rest) => {
    destroyFn({
      locator: {
        descriptor: {
          ...variables.locator.descriptor,
          tag_id: additionalDescriptor.tag_id || additionalDescriptor.Id,
          ...additionalDescriptor,
        },
      },
    }, {
      Id: additionalDescriptor && (additionalDescriptor.tag_id || additionalDescriptor.Id),
    }, ...rest);
  }, [destroyFn, variables]);

  const create = useCallback((newData, ...rest) => {
    createFn(variables, newData, ...rest);
  }, [createFn, variables]);

  const refetch = useCallback((parameter = {}, ...rest) => {
    refetchFn(
      {
        variables: {
          ...variables,
          ...parameter.variables,
        },
        payload: parameter.payload,
      },
      refetchConfig,
      ...rest
    );
  }, [refetchFn, refetchConfig, variables]);

  const { loadMore, hasMore, isLoading } = paginationMethods;

  return [data, {
    loadMore,
    hasMore,
    isLoading,
    create,
    update,
    destroy,
    refetch,
  }];
}
